import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, Loader2, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DataDeletionPage = () => {
  const lastUpdated = 'February 12, 2026';
  const [params] = useSearchParams();
  const code = params.get('code');
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(Boolean(code));
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!code) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/meta/data-deletion-status`, { params: { code } });
        if (!cancelled) setStatus(data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || 'Unable to look up deletion status.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [code]);

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-gray-200 bg-white/80 backdrop-blur-lg sticky top-0 z-30">
        <div className="max-w-screen-md mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="deletion-back-home">
            RealProp
          </Link>
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-black transition-colors" data-testid="deletion-back-link">
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>
        </div>
      </header>

      <main className="max-w-screen-md mx-auto px-6 py-12">
        <h1 className="text-4xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="deletion-title">
          User Data Deletion
        </h1>
        <p className="text-sm text-gray-500 mb-10" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Last updated: {lastUpdated}
        </p>

        {code && (
          <div className="mb-10 rounded-2xl border border-gray-200 bg-gray-50 p-5" data-testid="deletion-status-card">
            {loading ? (
              <div className="flex items-center gap-3 text-gray-700" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>Looking up your deletion request…</span>
              </div>
            ) : error ? (
              <div className="flex items-start gap-3 text-red-700" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <AlertCircle className="h-5 w-5 mt-0.5" />
                <div>
                  <div className="font-semibold">We couldn't find that request</div>
                  <div className="text-sm mt-1">{error}</div>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-3 text-gray-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <ShieldCheck className="h-5 w-5 mt-0.5 text-emerald-600" />
                <div>
                  <div className="font-semibold">
                    {status?.status === 'completed' ? 'Your data has been deleted' :
                     status?.status === 'pending_manual_review' ? 'Your request is being processed' :
                     'Request received'}
                  </div>
                  <div className="text-sm mt-1 text-gray-600">
                    Confirmation code: <span className="font-mono">{code}</span>
                  </div>
                  {status?.created_at && (
                    <div className="text-xs mt-1 text-gray-500">Received: {new Date(status.created_at).toLocaleString()}</div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="prose prose-gray max-w-none space-y-8" style={{ fontFamily: 'Manrope, sans-serif' }}>
          <section>
            <h2 className="text-xl font-bold tracking-tight mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>1. Your Right to Deletion</h2>
            <p className="text-gray-700 leading-relaxed">
              You can request that RealProp delete the personal data we store about you at any time. This includes your account profile, listings, generated content, social-account connections (Facebook, Instagram, Google Drive, Dropbox), and any cached data associated with those connections.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold tracking-tight mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>2. How to Request Deletion</h2>
            <h3 className="font-semibold mt-4 mb-1">Option A — In-app</h3>
            <p className="text-gray-700 leading-relaxed">
              Sign in at <a href="https://realprop.io" className="text-violet-600 underline">https://realprop.io</a>, go to <strong>Account Settings → Delete account</strong>. Confirming will permanently remove your account and all associated data within 30 days.
            </p>
            <h3 className="font-semibold mt-4 mb-1">Option B — Email request</h3>
            <p className="text-gray-700 leading-relaxed">
              Email <a href="mailto:support@realprop.io" className="text-violet-600 underline">support@realprop.io</a> from the address on your account with the subject line <em>"Delete my data"</em>. We will verify your identity and complete the deletion within 30 days.
            </p>
            <h3 className="font-semibold mt-4 mb-1">Option C — Remove RealProp from Facebook</h3>
            <p className="text-gray-700 leading-relaxed">
              You can remove RealProp from your Facebook account at any time:
            </p>
            <ol className="list-decimal pl-6 mt-2 space-y-1 text-gray-700">
              <li>Go to your Facebook <strong>Settings &amp; Privacy → Settings</strong>.</li>
              <li>Open <strong>Apps and Websites</strong>.</li>
              <li>Find <strong>RealProp</strong>, click <strong>Remove</strong>.</li>
            </ol>
            <p className="text-gray-700 leading-relaxed mt-3">
              When you remove the app, Facebook automatically notifies us and we delete all access tokens and Meta-derived data we hold for your Facebook user ID. You will receive a confirmation code that you can verify on this page.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold tracking-tight mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>3. What We Delete</h2>
            <ul className="list-disc pl-6 space-y-1 text-gray-700">
              <li>Your account profile, email, hashed password, and authentication tokens.</li>
              <li>Listings, drafts, campaigns, generated images, reels, and uploaded media.</li>
              <li>Stored OAuth tokens for Facebook, Instagram, Google Drive, and Dropbox.</li>
              <li>Any cached profile data (name, profile picture, page list) returned by Meta.</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-3">
              Aggregated, de-identified analytics that cannot be linked back to you may be retained. We may also retain limited records required by law (e.g., billing receipts for tax purposes).
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold tracking-tight mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>4. Timing</h2>
            <p className="text-gray-700 leading-relaxed">
              Deletion of Meta-derived data is automatic and typically completes within minutes. Full account deletion completes within 30 days of your request.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold tracking-tight mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>5. Contact</h2>
            <p className="text-gray-700 leading-relaxed">
              Questions or escalation: <a href="mailto:support@realprop.io" className="text-violet-600 underline">support@realprop.io</a>.
            </p>
          </section>
        </div>

        <div className="mt-16 pt-8 border-t border-gray-200 text-center text-sm text-gray-500" style={{ fontFamily: 'Manrope, sans-serif' }}>
          <Link to="/privacy" className="text-violet-600 underline mr-4" data-testid="link-to-privacy">Privacy Policy</Link>
          <Link to="/terms" className="text-violet-600 underline mr-4" data-testid="link-to-terms">Terms of Service</Link>
          <Link to="/" className="text-violet-600 underline">Home</Link>
        </div>
      </main>
    </div>
  );
};

export default DataDeletionPage;
